import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Platform } from '@app/classes';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { KanbanColumn } from '@app/interfaces/kanban-column.interface';
import { FunnelTemplateStage } from '@app/models/recruiting/funnel-template-stages.model';
import { FunnelTemplate } from '@app/models/recruiting/funnel-template.model';
import { PipelineShow } from '@app/modules/applicant-tracker/meta/view-contexts';
import {
    ApplicantIndividualMessageService,
    ApplicantService,
    SettingsIntegrationService,
} from '@app/modules/applicant-tracker/services';
import { FunnelService } from '@app/modules/applicant-tracker/services/funnel.service';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { FunnelStageAndMessageTemplate } from '@applicant-tracker/interfaces/funnel-stage-and-message-template.interface';
import { KanbanApplicant } from '@applicant-tracker/interfaces/kanban-applicant.interface';
import { RoutingStateService } from '@common/services/routing-state.service';
import { JobFunnelMessageTemplate } from '@models/recruiting/job-funnel-message-template.model';
import { JobFunnel } from '@models/recruiting/job-funnel.model';
import { JobPosting } from '@models/recruiting/job-posting.model';
@Component({
    templateUrl: './pipeline-show.template.html',
    styleUrls: ['./pipeline-show.style.scss'],
    providers: [FunnelService],
})
export class PipelineShowView implements OnInit, OnDestroy {
    @ViewChild('confirmDeleteDialog') confirmDeleteDialog: DeprecatedConfirmationDialogComponent;
    isLoading = true;
    jobPosting: JobPosting;
    kanban: KanbanColumn[];
    funnelTemplates: FunnelTemplate[] = [];
    funnelStages: FunnelTemplateStage[] = [];
    funnelStageAndMessageTemplates: FunnelStageAndMessageTemplate[];
    selectedFunnelTemplate: FunnelTemplate;
    droppedApplicantJobFunnelMessageTemplate?: JobFunnelMessageTemplate;
    expandApplyDropdown = false;
    isOpenApplyTemplate = false;
    isOpenSaveTemplate = false;
    isOpenAddFunnel = false;
    isChangeFunnelStageModalOpen = false;
    temporaryColumnName: string;
    temporaryFunnelTemplate: FunnelTemplate = new FunnelTemplate();
    funnelTemplateControl = new FormControl('', [Validators.required]);
    canAccessTemplates = false;
    kanbanDroppedApplicantId?: number;

    constructor(
        public route: ActivatedRoute,
        public funnelService: FunnelService,
        public applicantIndividualMessageService: ApplicantIndividualMessageService,
        private notify: NotifyService,
        private auth: AuthService,
        private applicantService: ApplicantService,
        private analyticService: AnalyticService,
        private integrationService: SettingsIntegrationService,
        private routingState: RoutingStateService
    ) {
        this.jobPosting = this.route.snapshot.data.models.get('jobPosting');
        this.funnelService.jobPosting = this.jobPosting;
        // Funnel view paginator is not required.
        this.applicantService.indexPaginator = undefined;
        this.applicantService.updateContext(PipelineShow);
    }

    async ngOnInit(): Promise<void> {
        // TODO: Remove this once the routing state service working correctly.
        this.routingState.history;

        this.canAccessTemplates = this.auth.isAdmin() || this.auth.can(Platform.recruiting.permission.recruiter);

        if (this.canAccessTemplates) {
            this.funnelTemplates = await this.funnelService.getFunnelTemplates();
        }

        // force reload to make sure it is up to date with kanban changes.
        this.loadJobPosting(this.jobPosting.id);

        $('.env-bar').fadeOut();
    }

    ngOnDestroy(): void {
        $('.env-bar').fadeIn();
    }

    onColumnOrderChanged(columns: KanbanColumn[]): void {
        this.kanban = columns;
    }

    onAddColumn(): void {
        this.temporaryColumnName = '';
        this.isOpenAddFunnel = true;
    }

    onColumnAdded(): void {
        this.funnelService
            .addColumn(this.jobPosting, this.temporaryColumnName)
            .then(() => this.loadJobPosting(this.jobPosting.id))
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.couldNotUpdateFunnel');
            });
        this.isOpenAddFunnel = false;
    }

    onColumnDeleted(columnId: number): void {
        this.funnelService
            .deleteColumn(columnId, this.jobPosting)
            .then(() => this.loadJobPosting(this.jobPosting.id))
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.couldNotDeleteFunnel');
            });
    }

    onColumnNameUpdated(update: { id: number; name: string }): void {
        const jobFunnel = this.jobPosting.jobFunnels.find((jobFunnel) => jobFunnel.id === update.id);
        jobFunnel.funnelStep = update.name;
        jobFunnel
            .param('company', this.auth.company.id)
            .param('jobPosting', this.jobPosting.id)
            .save()
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.couldNotUpdateFunnel');
            });
    }

    async applyFunnelTemplate(): Promise<void> {
        this.isLoading = true;
        this.isOpenApplyTemplate = true;
        this.funnelTemplates = await this.funnelService.getFunnelTemplates();
        this.isLoading = false;
    }

    openSaveFunnelTemplate(): void {
        this.isOpenSaveTemplate = true;
        this.temporaryFunnelTemplate = new FunnelTemplate();
        // Need to convert to the proper model to store the stages.

        this.funnelStageAndMessageTemplates = this.funnelService.convertToFunnelStageAndMessageTemplate(
            this.jobPosting.jobFunnels.filter((jobFunnel: JobFunnel) => jobFunnel.funnelStep !== 'Applied')
        );

        this.funnelStages = this.funnelService.convertToTemplateStages(
            this.jobPosting.jobFunnels.filter((jobFunnel: JobFunnel) => jobFunnel.funnelStep !== 'Applied')
        );
    }

    closeApplyTemplateModal(): void {
        this.isOpenApplyTemplate = false;
        this.selectedFunnelTemplate = undefined;
    }

    closeSaveTemplateModal(): void {
        this.isOpenSaveTemplate = false;
    }

    closeAddFunnelModal(): void {
        this.isOpenAddFunnel = false;
    }

    onCloseChangeFunnelStage(): void {
        this.isChangeFunnelStageModalOpen = false;
        this.notify.success('applicant-tracker.applicantStatusChanged');

        this.analyticService.trackEvent(
            AnalyticEvents.RecruitingJobPostingFunnelChangeApplicantFunnelStageClickSkipEmail
        );
    }

    onConfirmChangeFunnelStage(): void {
        this.applicantIndividualMessageService.sendApplicantIndividualMessage(
            this.kanbanDroppedApplicantId,
            this.droppedApplicantJobFunnelMessageTemplate
        );

        this.isChangeFunnelStageModalOpen = false;

        this.notify.success('applicant-tracker.applicantStatusChanged');

        this.notify.success({
            key: 'applicant-tracker.subjectEmailSentTOApplicant',
            params: { subject: this.droppedApplicantJobFunnelMessageTemplate.subject ?? '' },
        });

        this.analyticService.trackEvent(
            AnalyticEvents.RecruitingJobPostingFunnelChangeApplicantFunnelStageClickSendEmail
        );
    }

    onCardDropped(kanbanApplicant: KanbanApplicant): void {
        this.droppedApplicantJobFunnelMessageTemplate = this.kanban
            .filter((kanbanColumn: KanbanColumn) => {
                return kanbanColumn.data.id === kanbanApplicant.atsJobFunnelId;
            })
            .map((kanbanColumn: KanbanColumn) => {
                return kanbanColumn.data?.jobFunnelMessageTemplate;
            })[0];

        if (!this.droppedApplicantJobFunnelMessageTemplate) {
            return;
        }

        this.isChangeFunnelStageModalOpen = true;

        this.kanbanDroppedApplicantId = kanbanApplicant.id;
    }

    async onSaveApplyTemplate(): Promise<void> {
        try {
            this.analyticService.trackEvent(AnalyticEvents.RecruitingApplyPipelineFunnelTemplate);
            this.funnelStages = await this.funnelService.getFunnelStages(this.selectedFunnelTemplate.id);

            const newJobFunnels = this.funnelService.convertToJobFunnels(this.funnelStages);

            const newJobFunnelMessageTemplates = this.funnelService.convertToJobFunnelMessageTemplate(
                this.funnelStages
            );

            const jobFunnels = await Promise.all(this.funnelService.saveAllFunnels(newJobFunnels));

            const newJobFunnelMessageTemplatesPromises = newJobFunnelMessageTemplates
                .map((jobFunnelMessageTemplate, index) => {
                    if (jobFunnelMessageTemplate) {
                        jobFunnelMessageTemplate.atsJobFunnelId = jobFunnels[index].id;
                        return jobFunnelMessageTemplate.save();
                    }
                    return undefined;
                })
                .filter((promise): promise is Promise<JobFunnelMessageTemplate> => promise !== undefined);

            await Promise.all(newJobFunnelMessageTemplatesPromises);

            this.isOpenApplyTemplate = false;
            this.notify.success('applicant-tracker.templateSuccessfullyApplied');
            this.loadJobPosting(this.jobPosting.id);
        } catch (error) {
            this.notify.error(error);
            this.loadJobPosting(this.jobPosting.id);
            this.isOpenApplyTemplate = false;
        }
    }

    async onSaveFunnelTemplate(): Promise<void> {
        this.isLoading = true;
        this.analyticService.trackEvent(AnalyticEvents.RecruitingSavePipelineFunnelTemplate);
        this.temporaryFunnelTemplate.save().then(async (funnelTemplate: FunnelTemplate) => {
            const funnelTemplateStagesPromises = this.funnelStageAndMessageTemplates.map(
                (funnelStageAndMessageTemplate: FunnelStageAndMessageTemplate) => {
                    funnelStageAndMessageTemplate.funnelTemplateStage.atsFunnelTemplateId = funnelTemplate.id;
                    return funnelStageAndMessageTemplate.funnelTemplateStage.save();
                }
            );

            Promise.all(funnelTemplateStagesPromises).then(async (funnelTemplateStages: FunnelTemplateStage[]) => {
                await Promise.all(this.funnelMessageTemplatesPromises(funnelTemplateStages));
            });

            this.notify.success('applicant-tracker.templateSuccessfullySaved');
            this.closeSaveTemplateModal();
            this.isLoading = false;
        });
    }

    async onPause(pause: boolean): Promise<void> {
        try {
            this.isLoading = true;
            this.jobPosting = await this.integrationService.pauseJobIntegration(this.jobPosting.id, pause);
            this.notify.success({
                key: 'applicant-tracker.applicants.jobPostingHasBeenActionSuccessfully',
                params: { action: pause ? 'paused' : 'unpaused' },
            });
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    private loadJobPosting(jobPostingId: number): void {
        this.kanban = null;
        this.isLoading = true;
        JobPosting.param('company', this.auth.company.id)
            .with(['jobFunnels', 'jobFunnels.jobFunnelMessageTemplate'])
            .find(jobPostingId)
            .then(async (jobPosting: JobPosting) => {
                this.funnelService.jobPosting = jobPosting;
                this.jobPosting = jobPosting;
                this.jobPosting.setApplicantsToOnlyActive();
                const kanbanEntities = await this.funnelService.getKanbanEntities(jobPosting.id);
                this.kanban = this.jobPosting.toKanBan(kanbanEntities, jobPosting.jobFunnels);
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.couldNotLoadJobPosting');
                this.isLoading = false;
            });
    }

    private funnelMessageTemplatesPromises(funnelTemplateStages: FunnelTemplateStage[]): Promise<any>[] {
        return this.funnelStageAndMessageTemplates
            .map((funnelStageAndMessageTemplate: FunnelStageAndMessageTemplate, index) => {
                if (funnelStageAndMessageTemplate.funnelMessageTemplate) {
                    funnelStageAndMessageTemplate.funnelMessageTemplate.atsFunnelTemplateStageId =
                        funnelTemplateStages[index].id;
                    return funnelStageAndMessageTemplate.funnelMessageTemplate.save();
                }
            })
            .filter((funnelMessageTemplate) => {
                return funnelMessageTemplate !== undefined;
            });
    }
}
